/* Native collection pagination with an explicit, reversible pause lease. */
(() => {
  'use strict';
  if (window.HhCollectionPagination) return;
  const ROOT = '[data-hh-collection-root]';

  function init(root) {
    if (root.hhCollectionPagination && !root.hhCollectionPagination.getState().destroyed) return root.hhCollectionPagination;
    const meta = root.querySelector('[data-hh-page-metadata]');
    const container = root.querySelector('[data-product-container]');
    const mode = root.dataset.paginationType;
    let button = root.querySelector('[data-load-more]');
    const initialPage = Number(meta?.dataset.currentPage || 1);
    const pageCount = Number(meta?.dataset.totalPages || container?.dataset.totalPages || 1);
    const totalPages = Number.isInteger(pageCount) && pageCount >= 0 ? pageCount : 1;
    let currentPage = Number.isInteger(initialPage) && initialPage > 0 ? initialPage : 1;
    const pageParam = meta?.dataset.pageParam || 'page';
    const scope = location.pathname + location.search;
    const storageKey = 'collectionState:' + scope;
    let leases = 0, epoch = 0, lifecycle = 0, destroyed = false, suspended = false, observer = null;
    let leasedButton = null, originalDisabled = false;
    let queue = Promise.resolve(false), active = null, restoration = null, restoreRunning = false, lastFailure = false;
    const nativeMode = mode === 'loadmore' || mode === 'infinite';
    const alive = () => !destroyed && !suspended && root.isConnected &&
      (!container || root.contains(container)) && location.pathname + location.search === scope;
    const wrapper = () => container?.firstElementChild || container;
    const disconnect = () => { observer?.disconnect(); observer = null; };
    const forgetRestore = () => { try { sessionStorage.removeItem(storageKey); } catch { /* Storage can be disabled. */ } };

    function setupObserver() {
      if (!alive() || leases || restoration || lastFailure || mode !== 'infinite' || !button || observer || currentPage >= totalPages) return;
      observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting) && !leases && !restoration && !lastFailure) loadNext(false);
      }, {threshold: 1});
      observer.observe(button);
    }

    async function fetchNext() {
      if (!alive() || !button || !container || currentPage >= totalPages) return false;
      const nextPage = currentPage + 1;
      const abort = new AbortController();
      active = abort;
      const timeout = setTimeout(() => abort.abort('timeout'), 15000);
      button.classList.add('sf-spinner-loading');
      lastFailure = false;
      try {
        const url = new URL(location.href);
        url.searchParams.set('section_id', root.dataset.sectionId);
        url.searchParams.set(pageParam, String(nextPage));
        const response = await fetch(url.href, {
          method: 'GET', credentials: 'same-origin', mode: 'same-origin', redirect: 'error', signal: abort.signal,
          headers: {'X-Requested-With': 'XMLHttpRequest'}
        });
        if (!response.ok) throw Error('Failed to load more products');
        const html = await response.text();
        if (!alive() || abort.signal.aborted) return false;
        const template = document.createElement('template');
        template.innerHTML = html;
        const sections = template.content.querySelectorAll(ROOT);
        if (sections.length !== 1 || sections[0].dataset.sectionId !== root.dataset.sectionId ||
            sections[0].dataset.collectionHandle !== root.dataset.collectionHandle) throw Error('Incorrect collection section');
        const returnedMeta = sections[0].querySelector('[data-hh-page-metadata]');
        if (!returnedMeta || Number(returnedMeta.dataset.currentPage) !== nextPage ||
            Number(returnedMeta.dataset.totalPages) !== totalPages) throw Error('Collection pagination changed');
        const items = [...sections[0].querySelectorAll('[data-product-container] .sf__col-item')];
        if (!items.length) throw Error('Empty collection page');
        const ids = new Set([...wrapper().querySelectorAll('.sf__pcard[data-product-id]')].map(card => card.dataset.productId));
        const additions = [];
        for (const item of items) {
          const id = item.querySelector('.sf__pcard[data-product-id]')?.dataset.productId;
          if (!id) throw Error('Missing product identity');
          if (!ids.has(id)) {
            ids.add(id);
            for (const el of item.querySelectorAll('script,iframe,frame,frameset,object,embed,applet,base,meta,link,style,template')) {
              if (el.localName === 'script' && (el.getAttribute('type') || '').trim().toLowerCase() === 'application/json' && !el.hasAttribute('src')) {
                JSON.parse(el.textContent);
              } else el.remove();
            }
            additions.push(item);
          }
        }
        if (!alive()) return false;
        for (const item of additions) wrapper().appendChild(item);
        currentPage = nextPage;
        if (meta) meta.dataset.currentPage = String(currentPage);
        if (currentPage >= totalPages) {
          disconnect();
          const controls = button.closest('[data-hh-native-pagination]') || button.parentElement;
          controls?.remove();
          button = null;
        }
        return true;
      } catch {
        // Keep the native button available for an explicit retry; never spin restoration.
        if (!['pagehide', 'destroy'].includes(abort.signal.reason)) lastFailure = true;
        disconnect();
        return false;
      } finally {
        clearTimeout(timeout);
        active = null;
        if (alive() && button) button.classList.remove('sf-spinner-loading');
        if (!lastFailure) setupObserver();
      }
    }

    function loadNext(fromRestore = false) {
      if (!nativeMode || !alive() || leases || restoration && !fromRestore) return Promise.resolve(false);
      const generation = epoch;
      queue = queue.then(() => {
        if (!alive() || leases || generation !== epoch || restoration && !fromRestore) return false;
        return fetchNext();
      });
      return queue;
    }

    function finishRestore(scroll) {
      const saved = restoration;
      restoration = null;
      forgetRestore();
      if (scroll && alive() && Number.isFinite(saved?.scrollY)) window.scrollTo(0, saved.scrollY);
      setupObserver();
    }

    async function continueRestore() {
      if (!alive() || leases || !restoration || restoreRunning) return;
      restoreRunning = true;
      try {
        while (restoration && alive() && !leases) {
          if (lastFailure) { finishRestore(false); break; }
          const enoughItems = restoration.itemsCount > 0 && wrapper()?.querySelectorAll('.sf__col-item').length >= restoration.itemsCount;
          if (!button || currentPage >= restoration.page || enoughItems) { finishRestore(true); break; }
          const success = await loadNext(true);
          if (!alive() || leases) break;
          if (!success) { finishRestore(false); break; }
        }
      } finally { restoreRunning = false; }
    }

    function resume() {
      if (!alive() || leases) return;
      if (restoration) continueRestore();
      else setupObserver();
    }

    function saveState() {
      if (!alive() || !container || !nativeMode) return;
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({
          page: restoration?.page ?? currentPage, scrollY: restoration?.scrollY ?? window.scrollY,
          itemsCount: restoration?.itemsCount || wrapper().querySelectorAll('.sf__col-item').length
        }));
      } catch { /* Storage can be disabled. */ }
    }

    const click = () => loadNext(false);
    const saveOnLink = event => {
      const link = event.target.closest?.('a');
      if (link && (!link.target || link.target === '_self') && root.contains(link)) saveState();
    };
    const pagehide = () => { saveState(); suspended = true; epoch++; lifecycle++; disconnect(); active?.abort('pagehide'); };
    const pageshow = () => {
      const generation = lifecycle;
      queue.then(() => {
        if (destroyed || generation !== lifecycle) return;
        suspended = false;
        queueMicrotask(resume);
      });
    };
    const restoreButton = () => {
      if (leasedButton && root.isConnected && root.contains(leasedButton)) leasedButton.disabled = originalDisabled;
      leasedButton = null;
    };

    const controller = {
      pause() {
        if (!leases && alive() && button) {
          leasedButton = button; originalDisabled = button.disabled; button.disabled = true;
        }
        leases++; epoch++; disconnect();
        let released = false;
        return queue.then(() => () => {
          if (released) return;
          released = true; leases--;
          if (!leases) { restoreButton(); queueMicrotask(resume); }
        });
      },
      loadNext: () => loadNext(false),
      getState: () => ({currentPage, totalPages, paused: leases > 0, loading: active !== null,
        restoring: restoration !== null, destroyed}),
      destroy() {
        if (destroyed) return;
        restoreButton();
        destroyed = true; epoch++; lifecycle++; disconnect(); active?.abort('destroy'); restoration = null;
        button?.removeEventListener('click', click);
        document.removeEventListener('click', saveOnLink);
        window.removeEventListener('pagehide', pagehide);
        window.removeEventListener('pageshow', pageshow);
      }
    };
    root.hhCollectionPagination = controller;
    if (button && nativeMode) {
      button.dataset.fallbackBound = 'true';
      button.addEventListener('click', click);
    }
    document.addEventListener('click', saveOnLink);
    window.addEventListener('pagehide', pagehide);
    window.addEventListener('pageshow', pageshow);
    if (nativeMode) {
      try {
        const saved = JSON.parse(sessionStorage.getItem(storageKey));
        if (saved && Number.isInteger(saved.page) && saved.page > 0) restoration = {
          page: Math.min(saved.page, totalPages),
          itemsCount: Number.isFinite(saved.itemsCount) ? saved.itemsCount : 0,
          scrollY: Number.isFinite(saved.scrollY) ? saved.scrollY : null
        };
      } catch { forgetRestore(); }
    }
    root.dispatchEvent(new CustomEvent('hh:collection-pagination-ready', {bubbles: true}));
    resume();
    return controller;
  }

  function boot(scope = document) {
    if (scope.matches?.(ROOT)) init(scope);
    scope.querySelectorAll(ROOT).forEach(init);
  }
  window.HhCollectionPagination = {init, boot};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), {once: true});
  else boot();
  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.matches?.(ROOT)) event.target.hhCollectionPagination?.destroy();
    event.target.querySelectorAll(ROOT).forEach(root => root.hhCollectionPagination?.destroy());
  });
})();
